import React, { useEffect } from 'react';
import StatusBadge from './StatusBadge';
import PaymentCard from './PaymentCard';

interface IRecebimento {
    id: number;
    valor: string;
    vencimento: string;
    status: 'R' | 'A';
    descricao: string;
    tipo_recebimento: 'Transferencia' | 'Gateway' | 'Boleto' | 'Cartão' | 'PIX';
}

interface IESim {
    qrcode?: string;
    qrcode_base64?: string;
    sm_dp_address?: string;
    activation_code?: string;
}

interface IPortabilidadeStatus {
    status?: string;
    protocol_id?: string;
    phone_number_ported?: string;
    phone_number_temporary?: string;
    customer_name?: string;
    created_date?: string;
    scheduled_date?: string;
    authorized?: boolean;
    donor_provider?: string;
    recipient_provider?: string;
}

interface IPortabilidade {
    ativa?: boolean;
    numero_origem?: string;
    data_agendamento?: string;
    gateway_id?: string;
    status_api?: IPortabilidadeStatus;
}

interface IMvnoData {
    status: false | 'PENDENTE' | 'CONFIGURANDO' | 'AGUARDANDO_ATIVACAO' | 'AGUARDANDO_PORTABILIDADE' | 'CONFIGURANDO_PORTABILIDADE' | 'ATIVO' | 'SUSPENSO' | 'CANCELADO';
    chip_type?: 'ESIM' | 'FISICO';
    esim?: IESim | null;
    iccid?: string | null;
    linha_ativa: boolean | string;
    phone_number?: string | null;
    portabilidade?: IPortabilidade | null;
}

interface ICancelamentoAgendado {
    id: number;
    data_cancelamento: string;
    motivo: string;
    cancelar_internet: boolean;
    cancelar_tv: boolean;
    cancelar_mvno: boolean;
    status: 'PENDENTE' | 'PROCESSADO' | 'CANCELADO';
}

interface IAssinatura {
    id: number;
    contrato: string;
    data_ativacao: string;
    data_expiracao: string;
    data_pagamento: string;
    status: string;
    valor_plano?: string | null;
    a_receber: IRecebimento[];
    tipo_plano: 'TELEFONIA_MOVEL' | 'STREAMING' | 'INTERNET' | 'outros';
    mvno?: IMvnoData;
    cancelamento_agendado?: ICancelamentoAgendado | null;
}

interface IPortabilidadeInput {
    ativar: boolean;
    numeroAtual: string;
    dataAgendamento: string;
    mudancaTitular: boolean;
    novoTitular: string;
    cpfNovoTitular: string;
}

interface SubscriptionCardProps {
    assinatura: IAssinatura;
    showQRCode: boolean;
    selectedAreaCode: string;
    activatingMvno: boolean;
    portabilidade?: IPortabilidadeInput;
    portabilidadeDatas?: Array<{ start: string; end: string }>;
    loadingPortabilidadeDatas?: boolean;
    onCancelClick: () => void;
    onToggleQRCode: () => void;
    onAreaCodeChange: (code: string) => void;
    onAtivarMVNO: (chipType: 'ESIM' | 'FISICO') => void;
    onPagar: (id: number, contratoId: number, valor: string) => void;
    onPortabilidadeChange?: (data: Partial<IPortabilidadeInput>) => void;
    onCarregarDatasPortabilidade?: () => void;
    onConfigurarPortabilidade?: () => void;
    configurandoPortabilidade?: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
    assinatura,
    showQRCode,
    selectedAreaCode,
    activatingMvno,
    portabilidade,
    portabilidadeDatas,
    loadingPortabilidadeDatas,
    onCancelClick,
    onToggleQRCode,
    onAreaCodeChange,
    onAtivarMVNO,
    onPagar,
    onPortabilidadeChange,
    onCarregarDatasPortabilidade,
    onConfigurarPortabilidade,
    configurandoPortabilidade = false
}) => {
    const formatDate = (dateString: string) => {
        if (!dateString || dateString === '0000-00-00') return 'N/A';
        return dateString;
    };

    const formatTipoPlano = (tipo: string) => {
        const map: Record<string, string> = {
            'TELEFONIA_MOVEL': 'Telefonia Móvel',
            'STREAMING': 'Streaming',
            'INTERNET': 'Internet',
            'outros': 'Outros'
        };
        return map[tipo] || tipo.replace(/_/g, ' ');
    };

    const formatDateTime = (value?: string) => {
        if (!value) return 'N/A';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return parsed.toLocaleString('pt-BR');
    };

    useEffect(() => {
        if (
            assinatura.mvno?.status === 'CONFIGURANDO_PORTABILIDADE' &&
            !portabilidadeDatas?.length &&
            !loadingPortabilidadeDatas &&
            onCarregarDatasPortabilidade
        ) {
            onCarregarDatasPortabilidade();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assinatura.mvno?.status]);

    const portStatus = assinatura.mvno?.portabilidade?.status_api;
    const portabilidadeEmProcessamento = !!portStatus?.status && portStatus.status !== 'success';
    const showEsimSection = assinatura.mvno?.chip_type === 'ESIM' && !!assinatura.mvno?.esim?.qrcode_base64;

    const canCancel = !assinatura.cancelamento_agendado &&
                      assinatura.status !== 'C' &&
                      assinatura.status !== 'CANCELADO';

    return (
        <div className="sub-card">
            {/* Card Header */}
            <div className="sub-card-header">
                <div className="sub-card-title-wrap">
                    <h3 className="sub-card-contract">{assinatura.contrato}</h3>
                    <div className="sub-card-badges">
                        <StatusBadge status={assinatura.status} />
                        <span className="sub-card-tipo-badge">{formatTipoPlano(assinatura.tipo_plano)}</span>
                    </div>
                </div>
                {canCancel && (
                    <button className="sub-card-cancel-btn" onClick={onCancelClick}>
                        Cancelar
                    </button>
                )}
            </div>

            {/* Card Body */}
            <div className="sub-card-body">

                {/* Scheduled Cancellation Alert */}
                {assinatura.cancelamento_agendado && (
                    <div className="sub-cancel-alert">
                        <p className="sub-cancel-alert-title">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#FF9800"/></svg>
                            Cancelamento Agendado
                        </p>
                        <p className="sub-cancel-alert-detail"><strong>Data:</strong> {assinatura.cancelamento_agendado.data_cancelamento}</p>
                        <p className="sub-cancel-alert-detail"><strong>Motivo:</strong> {assinatura.cancelamento_agendado.motivo}</p>
                        <p className="sub-cancel-alert-detail"><strong>Status:</strong> {assinatura.cancelamento_agendado.status}</p>
                        {(assinatura.cancelamento_agendado.cancelar_internet || assinatura.cancelamento_agendado.cancelar_tv || assinatura.cancelamento_agendado.cancelar_mvno) && (
                            <p className="sub-cancel-alert-detail" style={{ marginTop: 4 }}>
                                <strong>Serviços:</strong>{' '}
                                {[
                                    assinatura.cancelamento_agendado.cancelar_internet && 'Internet',
                                    assinatura.cancelamento_agendado.cancelar_tv && 'TV',
                                    assinatura.cancelamento_agendado.cancelar_mvno && 'Telefonia Móvel'
                                ].filter(Boolean).join(', ')}
                            </p>
                        )}
                    </div>
                )}

                {/* Info Grid */}
                <div className="sub-info-grid">
                    <div className="sub-info-item">
                        <span className="sub-info-label">Valor do Plano</span>
                        <span className="sub-info-value">{assinatura.valor_plano ? `R$ ${assinatura.valor_plano}` : 'N/A'}</span>
                    </div>
                    {/* <div className="sub-info-item">
                        <span className="sub-info-label">Ativação</span>
                        <span className="sub-info-value">{formatDate(assinatura.data_ativacao)}</span>
                    </div>
                    <div className="sub-info-item">
                        <span className="sub-info-label">Expiração</span>
                        <span className="sub-info-value">{formatDate(assinatura.data_expiracao)}</span>
                    </div> */}
                    <div className="sub-info-item">
                        <span className="sub-info-label">Pagamento</span>
                        <span className="sub-info-value">{formatDate(assinatura.data_pagamento)}</span>
                    </div>
                </div>

                {/* MVNO Section */}
                {assinatura.tipo_plano === 'TELEFONIA_MOVEL' && assinatura.mvno?.status !== false && (
                    <div className="sub-mvno-section">
                        <p className="sub-section-label">Telefonia Móvel</p>

                        {assinatura.mvno?.status === 'PENDENTE' && (
                            <div className="sub-status-box warning">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/></svg>
                                <span>Sua assinatura está pendente. A configuração da linha móvel será liberada após a confirmação do pagamento.</span>
                            </div>
                        )}

                        {assinatura.mvno?.status === 'AGUARDANDO_ATIVACAO' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div className="sub-status-box success">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/></svg>
                                    <span>Linha configurada — {assinatura.mvno?.chip_type}</span>
                                </div>
                                {assinatura.mvno?.phone_number && (
                                    <span className="sub-phone-number">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="currentColor"/></svg>
                                        {assinatura.mvno.phone_number}
                                    </span>
                                )}
                            </div>
                        )}

                        {assinatura.mvno?.status === 'AGUARDANDO_PORTABILIDADE' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div className="sub-status-box purple">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="currentColor"/></svg>
                                    <span>Aguardando portabilidade</span>
                                </div>
                                {assinatura.mvno?.phone_number && (
                                    <span className="sub-phone-number">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="currentColor"/></svg>
                                        Temporário: {assinatura.mvno.phone_number}
                                    </span>
                                )}
                                {portabilidadeEmProcessamento && (
                                    <div className="sub-status-box warning">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/></svg>
                                        <span>Portabilidade em processamento. Aguarde a conclusão.</span>
                                    </div>
                                )}
                                {assinatura.mvno?.portabilidade && (
                                    <div className="sub-port-grid">
                                        {assinatura.mvno.portabilidade.numero_origem && (
                                            <div className="sub-port-item"><strong>Número de origem</strong>{assinatura.mvno.portabilidade.numero_origem}</div>
                                        )}
                                        {portStatus?.phone_number_ported && (
                                            <div className="sub-port-item"><strong>Número portado</strong>{portStatus.phone_number_ported}</div>
                                        )}
                                        {portStatus?.phone_number_temporary && (
                                            <div className="sub-port-item"><strong>Número temporário</strong>{portStatus.phone_number_temporary}</div>
                                        )}
                                        {assinatura.mvno.portabilidade.data_agendamento && (
                                            <div className="sub-port-item"><strong>Agendada para</strong>{formatDateTime(assinatura.mvno.portabilidade.data_agendamento)}</div>
                                        )}
                                        {portStatus?.scheduled_date && (
                                            <div className="sub-port-item"><strong>Janela agendada</strong>{formatDateTime(portStatus.scheduled_date)}</div>
                                        )}
                                        {assinatura.mvno.portabilidade.gateway_id && (
                                            <div className="sub-port-item"><strong>Gateway ID</strong>{assinatura.mvno.portabilidade.gateway_id}</div>
                                        )}
                                        {portStatus?.protocol_id && (
                                            <div className="sub-port-item"><strong>Protocolo</strong>{portStatus.protocol_id}</div>
                                        )}
                                        {portStatus?.customer_name && (
                                            <div className="sub-port-item"><strong>Titular</strong>{portStatus.customer_name}</div>
                                        )}
                                        {portStatus?.donor_provider && (
                                            <div className="sub-port-item"><strong>Operadora origem</strong>{portStatus.donor_provider}</div>
                                        )}
                                        {portStatus?.recipient_provider && (
                                            <div className="sub-port-item"><strong>Operadora destino</strong>{portStatus.recipient_provider}</div>
                                        )}
                                        {portStatus?.created_date && (
                                            <div className="sub-port-item"><strong>Criado em</strong>{formatDateTime(portStatus.created_date)}</div>
                                        )}
                                        {portStatus?.authorized !== undefined && (
                                            <div className="sub-port-item"><strong>Autorizado</strong>{portStatus.authorized ? 'Sim' : 'Não'}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* e-SIM QR Code */}
                        {(assinatura.mvno?.status === 'AGUARDANDO_ATIVACAO' || assinatura.mvno?.status === 'AGUARDANDO_PORTABILIDADE') && showEsimSection && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <button className="sub-qr-toggle-btn" onClick={onToggleQRCode}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM17 13h2v2h-2zM19 15h2v2h-2zM17 17h2v2h-2zM19 19h2v2h-2zM15 19h2v2h-2zM13 21h2v-2h-2z" fill="currentColor"/></svg>
                                    {showQRCode ? 'Ocultar QR Code' : 'Ver QR Code do e-SIM'}
                                </button>

                                {showQRCode && (
                                    <div className="sub-qr-box">
                                        <h5>Escaneie com seu dispositivo para ativar o e-SIM</h5>
                                        <img
                                            src={assinatura.mvno?.esim?.qrcode_base64}
                                            alt="QR Code e-SIM"
                                        />
                                        <div className="sub-qr-info">
                                            {assinatura.mvno?.esim?.sm_dp_address && (
                                                <div className="sub-qr-info-item">
                                                    <strong>SM-DP+ Address:</strong>
                                                    <code className="sub-qr-code-text">{assinatura.mvno.esim.sm_dp_address}</code>
                                                </div>
                                            )}
                                            {assinatura.mvno?.esim?.activation_code && (
                                                <div className="sub-qr-info-item">
                                                    <strong>Activation Code:</strong>
                                                    <code className="sub-qr-code-text">{assinatura.mvno.esim.activation_code}</code>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* CONFIGURANDO - Setup form */}
                        {assinatura.mvno?.status === 'CONFIGURANDO' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <div className="sub-status-box warning">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/></svg>
                                    <span>Sua linha ainda não foi configurada. Selecione o DDD e o tipo de chip abaixo.</span>
                                </div>

                                <div>
                                    <span className="sub-field-label">Código de Área (DDD)</span>
                                    <div className="sub-select-wrap">
                                        <select
                                            className="sub-select"
                                            value={selectedAreaCode}
                                            onChange={(e) => onAreaCodeChange(e.target.value)}
                                        >
                                            <option value="">Selecione o DDD</option>
                                            <option value="11">11 — São Paulo</option>
                                            <option value="21">21 — Rio de Janeiro</option>
                                            <option value="31">31 — Belo Horizonte</option>
                                            <option value="41">41 — Curitiba</option>
                                            <option value="48">48 — Florianópolis</option>
                                            <option value="51">51 — Porto Alegre</option>
                                            <option value="61">61 — Brasília</option>
                                            <option value="71">71 — Salvador</option>
                                            <option value="81">81 — Recife</option>
                                            <option value="85">85 — Fortaleza</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Portabilidade Toggle */}
                                <div>
                                    <span className="sub-field-label">Portabilidade</span>
                                    <label className="sub-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={portabilidade?.ativar ?? false}
                                            onChange={(e) => {
                                                onPortabilidadeChange?.({ ativar: e.target.checked });
                                                if (e.target.checked && onCarregarDatasPortabilidade) {
                                                    onCarregarDatasPortabilidade();
                                                }
                                            }}
                                        />
                                        <span className="sub-toggle-text">
                                            <strong>Quero portar meu número</strong>
                                            <small>Transfira seu número atual para esta linha</small>
                                        </span>
                                    </label>
                                </div>

                                {/* Portabilidade Form */}
                                {portabilidade?.ativar && (
                                    <div className="sub-port-form">
                                        <p className="sub-port-form-title">Informações de portabilidade</p>

                                        <div>
                                            <span className="sub-field-label">Número atual (com DDD)</span>
                                            <input
                                                className="sub-input"
                                                type="tel"
                                                placeholder="Ex: (11) 99999-9999"
                                                value={portabilidade.numeroAtual}
                                                onChange={(e) => onPortabilidadeChange?.({ numeroAtual: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <span className="sub-field-label">Data de agendamento</span>
                                            {portabilidadeDatas && portabilidadeDatas.length > 0 ? (
                                                <div className="sub-select-wrap">
                                                    <select
                                                        className="sub-select"
                                                        value={portabilidade.dataAgendamento}
                                                        onChange={(e) => onPortabilidadeChange?.({ dataAgendamento: e.target.value })}
                                                    >
                                                        <option value="">Selecione uma data</option>
                                                        {portabilidadeDatas.map((d, i) => (
                                                            <option key={i} value={d.start}>
                                                                {new Date(d.start).toLocaleString('pt-BR')} — {new Date(d.end).toLocaleString('pt-BR')}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <button
                                                    className="sub-btn-secondary"
                                                    onClick={onCarregarDatasPortabilidade}
                                                    disabled={loadingPortabilidadeDatas}
                                                >
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/></svg>
                                                    {loadingPortabilidadeDatas ? 'Carregando...' : 'Carregar datas disponíveis'}
                                                </button>
                                            )}
                                        </div>

                                        <div>
                                            <label className="sub-toggle-label">
                                                <input
                                                    type="checkbox"
                                                    checked={portabilidade.mudancaTitular}
                                                    onChange={(e) => onPortabilidadeChange?.({ mudancaTitular: e.target.checked })}
                                                />
                                                <span className="sub-toggle-text">
                                                    <strong>Mudança de titular</strong>
                                                    <small>O número pertence a outra pessoa</small>
                                                </span>
                                            </label>
                                        </div>

                                        {portabilidade.mudancaTitular && (
                                            <>
                                                <div>
                                                    <span className="sub-field-label">Nome do titular atual</span>
                                                    <input
                                                        className="sub-input"
                                                        type="text"
                                                        placeholder="Nome completo"
                                                        value={portabilidade.novoTitular}
                                                        onChange={(e) => onPortabilidadeChange?.({ novoTitular: e.target.value })}
                                                    />
                                                </div>
                                                <div>
                                                    <span className="sub-field-label">CPF do titular atual</span>
                                                    <input
                                                        className="sub-input"
                                                        type="text"
                                                        placeholder="000.000.000-00"
                                                        value={portabilidade.cpfNovoTitular}
                                                        onChange={(e) => onPortabilidadeChange?.({ cpfNovoTitular: e.target.value })}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <span className="sub-field-label">Tipo de Chip</span>
                                    <div className="sub-chip-buttons">
                                        <button
                                            className="sub-chip-btn esim"
                                            onClick={() => onAtivarMVNO('ESIM')}
                                            disabled={activatingMvno}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 14H8V8h4v8zm4 0h-2V8h2v8z" fill="currentColor"/></svg>
                                            {activatingMvno ? 'Ativando...' : 'Ativar e-SIM'}
                                        </button>
                                        <button
                                            className="sub-chip-btn fisico"
                                            onClick={() => onAtivarMVNO('FISICO')}
                                            disabled={activatingMvno}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" fill="currentColor"/></svg>
                                            {activatingMvno ? 'Ativando...' : 'Chip Físico'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CONFIGURANDO_PORTABILIDADE - Portability config form */}
                        {assinatura.mvno?.status === 'CONFIGURANDO_PORTABILIDADE' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <div className="sub-status-box purple">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="currentColor"/></svg>
                                    <span>Linha ativa. Configure a portabilidade do seu número abaixo.</span>
                                </div>

                                {assinatura.mvno?.phone_number && (
                                    <span className="sub-phone-number">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="currentColor"/></svg>
                                        Temporário: {assinatura.mvno.phone_number}
                                    </span>
                                )}

                                <div className="sub-port-form">
                                    <p className="sub-port-form-title">Informações de portabilidade</p>

                                    <div>
                                        <span className="sub-field-label">Número atual (com DDD)</span>
                                        <input
                                            className="sub-input"
                                            type="tel"
                                            placeholder="Ex: (11) 99999-9999"
                                            value={portabilidade?.numeroAtual ?? ''}
                                            onChange={(e) => onPortabilidadeChange?.({ numeroAtual: e.target.value })}
                                        />
                                        {(() => {
                                            const tempDDD = assinatura.mvno?.phone_number?.replace(/\D/g, '').slice(0, 2);
                                            const typedDDD = (portabilidade?.numeroAtual ?? '').replace(/\D/g, '').slice(0, 2);
                                            if (tempDDD && typedDDD.length === 2 && typedDDD !== tempDDD) {
                                                return (
                                                    <span className="sub-input-warning">
                                                        ⚠ O DDD informado ({typedDDD}) é diferente do DDD do número temporário ({tempDDD})
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>

                                    <div>
                                        <span className="sub-field-label">Data de agendamento</span>
                                        {portabilidadeDatas && portabilidadeDatas.length > 0 ? (
                                            <div className="sub-select-wrap">
                                                <select
                                                    className="sub-select"
                                                    value={portabilidade?.dataAgendamento ?? ''}
                                                    onChange={(e) => onPortabilidadeChange?.({ dataAgendamento: e.target.value })}
                                                >
                                                    <option value="">Selecione uma data</option>
                                                    {portabilidadeDatas.map((d, i) => (
                                                        <option key={i} value={d.start}>
                                                            {new Date(d.start).toLocaleString('pt-BR')} — {new Date(d.end).toLocaleString('pt-BR')}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <button
                                                className="sub-btn-secondary"
                                                onClick={onCarregarDatasPortabilidade}
                                                disabled={loadingPortabilidadeDatas}
                                            >
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/></svg>
                                                {loadingPortabilidadeDatas ? 'Carregando...' : 'Carregar datas disponíveis'}
                                            </button>
                                        )}
                                    </div>

                                    <div>
                                        <label className="sub-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={portabilidade?.mudancaTitular ?? false}
                                                onChange={(e) => onPortabilidadeChange?.({ mudancaTitular: e.target.checked })}
                                            />
                                            <span className="sub-toggle-text">
                                                <strong>Mudança de titular</strong>
                                                <small>O número pertence a outra pessoa</small>
                                            </span>
                                        </label>
                                    </div>

                                    {portabilidade?.mudancaTitular && (
                                        <>
                                            <div>
                                                <span className="sub-field-label">Nome do titular atual</span>
                                                <input
                                                    className="sub-input"
                                                    type="text"
                                                    placeholder="Nome completo"
                                                    value={portabilidade.novoTitular}
                                                    onChange={(e) => onPortabilidadeChange?.({ novoTitular: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <span className="sub-field-label">CPF do titular atual</span>
                                                <input
                                                    className="sub-input"
                                                    type="text"
                                                    placeholder="000.000.000-00"
                                                    value={portabilidade.cpfNovoTitular}
                                                    onChange={(e) => onPortabilidadeChange?.({ cpfNovoTitular: e.target.value })}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <button
                                    className="sub-chip-btn esim"
                                    style={{ width: '100%' }}
                                    onClick={onConfigurarPortabilidade}
                                    disabled={configurandoPortabilidade}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    {configurandoPortabilidade ? 'Solicitando...' : 'Solicitar Portabilidade'}
                                </button>
                            </div>
                        )}

                        {assinatura.mvno?.status === 'SUSPENSO' && (
                            <div className="sub-status-box error">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="currentColor"/></svg>
                                <span>Sua linha está suspensa. Entre em contato com o suporte para mais informações.</span>
                            </div>
                        )}

                        {assinatura.mvno?.status === 'CANCELADO' && (
                            <div className="sub-status-box muted">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" fill="currentColor"/></svg>
                                <span>Sua linha foi cancelada.</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Payments */}
                {assinatura.a_receber && assinatura.a_receber.length > 0 && (
                    <div className="sub-payments-section">
                        <p className="sub-section-label green">Pagamentos</p>
                        {assinatura.a_receber.map((recebimento) => (
                            <PaymentCard
                                key={recebimento.id}
                                recebimento={recebimento}
                                contratoId={assinatura.id}
                                onPagar={onPagar}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionCard;
